// eslint-disable-next-line no-unused-vars
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContext } from 'use-context-selector';
import { node } from 'prop-types';
import noop from 'lodash/noop';
import omit from 'lodash/omit';

import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import { API_OFFER_PRODUCTS_DETAILS } from 'Constants';
import isOfferExpired, {
  clampMarkupPercent,
  generateOfferId,
  generateOfferNumber,
  getOfferValidUntil,
  getUnitOfferPriceInRon,
  isProductUnavailableForOffer,
  roundMoney,
  VAT_RATE
} from 'Utils/offers';

const LEGACY_STORAGE_KEY = 'nod-webshop-private-offers';
const OFFERS_LIMIT = 20;
const EMPTY_OFFERS_META = {
  page: 1,
  limit: OFFERS_LIMIT,
  totalPages: 0,
  totalItems: 0
};

const getStorageKey = userId => userId === null || userId === undefined
  ? null
  : `${ LEGACY_STORAGE_KEY }:${ userId }`;

export const OffersContext = createContext({
  offers: [],
  meta: EMPTY_OFFERS_META,
  activeOffer: null,
  activeOfferId: null,
  activeOfferStorage: null,
  isHydrated: false,
  currentDestination: null,
  createOffer: noop,
  updateOffer: noop,
  addProductToOffer: noop,
  updateOfferProduct: noop,
  removeProductFromOffer: noop,
  deleteOffer: noop,
  moveOfferToSaved: noop,
  setOffersPage: noop,
  setActiveOffer: noop,
  setCurrentDestination: noop
});

const normalizeOfferId = id => {
  if (typeof id !== 'string') return id;

  const legacyPrivateId = id.match(/^private-offer-(.+)$/);

  return legacyPrivateId ? legacyPrivateId[1] : id;
};

const getProductQuantity = product => product.quantity || product.minQuantity || 1;

const getProductId = product => product.productId || product.id;

const getProductValidationError = (productIds, response) => {
  const responseProducts = response?.products;
  if (!responseProducts || typeof responseProducts !== 'object') {
    // Empty message lets callers fall back to their translated error.
    return new Error();
  }
  const invalidProducts = Object.fromEntries(productIds
    .map(productId => {
      const product = responseProducts[productId];
      if (product?.status) return [productId, product];
      if (!product) return [productId, { status: 'PRODUCT_NOT_FOUND' }];
      return null;
    })
    .filter(Boolean));

  if (!Object.keys(invalidProducts).length) return null;

  const error = new Error('Product is not available for offers.');
  error.status = 422;
  error.info = { products: invalidProducts };
  return error;
};

const getProductPictureUrl = product => {
  const candidates = [
    product.pictureUrl,
    product.picture,
    product.pictures?.at(0)?.urlThumbnailPicture,
    product.pictures?.at(0)?.urlOverlayPicture,
    product.primaryImage?.URLs?.thumbnail
  ];

  return candidates
    .map(candidate => Array.isArray(candidate) ? candidate.find(Boolean) : candidate)
    .find(Boolean) || null;
};

const normalizeOfferProduct = product => ({
  ...product,
  title: product.title || product.name,
  pictureUrl: getProductPictureUrl(product),
  quantity: getProductQuantity(product),
  markupPercent: product.markupPercent ?? product.markup ?? null,
  unitCustomPrice: product.unitCustomPrice ?? product.customPrice ?? null
});

const hasOfferProduct = (offer, product) => {
  const productId = getProductId(product);
  return offer.items.some(item => getProductId(item) === productId);
};

const getAddProductBlockedResult = (offer, product, options) => {
  if (isOfferExpired(offer)) {
    return { offer, expired: true };
  }
  if (isProductUnavailableForOffer(product)) {
    return { offer, unavailable: true };
  }
  const stockValue = Number(product.stockValue ?? product.stock ?? 0);
  const minQuantity = Math.max(1, Number(product.minQuantity || 1));
  if (!options.allowOutOfStock && stockValue < minQuantity) {
    return { offer, outOfStock: true };
  }
  if (hasOfferProduct(offer, product)) {
    return { offer, alreadyExists: true };
  }
  return null;
};

const migrateOfferProduct = product => {
  const { markup, customPrice, ...productData } = product;
  return {
    ...productData,
    quantity: getProductQuantity(product),
    markupPercent: product.markupPercent ?? markup ?? null,
    unitCustomPrice: product.unitCustomPrice ?? customPrice ?? null
  };
};

const withOfferSummary = offer => {
  const items = (offer.items || []).map(normalizeOfferProduct);
  const exchangeRate = Number(offer.exchangeRate) > 0 ? Number(offer.exchangeRate) : 1;
  const totalWithoutVat = items.reduce((sum, item) => {
    const unitOfferPrice = roundMoney(
      getUnitOfferPriceInRon(item, offer.defaultMarkupPercent) / exchangeRate
    );
    return sum + roundMoney(unitOfferPrice * Number(item.quantity || 0));
  }, 0);
  const roundedTotalWithoutVat = roundMoney(totalWithoutVat);
  const totalVat = roundMoney(roundedTotalWithoutVat * VAT_RATE);

  return {
    ...offer,
    items,
    numberOfProducts: items.length,
    totalWithoutVat: roundedTotalWithoutVat,
    totalVat,
    totalWithVat: roundMoney(roundedTotalWithoutVat + totalVat)
  };
};

const normalizeOffer = offer => {
  const {
    client,
    currency,
    globalMarkup,
    products,
    ...offerData
  } = offer;
  const currencyId = String(offer.currencyId || currency || 'ron').toLowerCase();
  return withOfferSummary({
    ...omit(offerData, ['exchangeRates']),
    id: normalizeOfferId(offer.id),
    clientName: offer.clientName ?? client ?? '',
    clientEmail: offer.clientEmail ?? '',
    currencyId,
    exchangeRate: Number(offer.exchangeRate) > 0
      ? Number(offer.exchangeRate)
      : 1,
    defaultMarkupPercent: clampMarkupPercent(
      offer.defaultMarkupPercent ?? globalMarkup ?? 0
    ),
    items: (offer.items || products || []).map(migrateOfferProduct)
  });
};

const OffersProvider = memo(({ children }) => {
  const fetcher = useFetch();
  const { data: { userId } = {} } = useUser();
  const [offers, setOffers] = useState([]);
  const [offersPage, setOffersPageState] = useState(1);
  const [activeOfferId, setActiveOfferId] = useState(null);
  const [activeOfferStorage, setActiveOfferStorage] = useState(null);
  const [currentDestination, setCurrentDestinationState] = useState(null);
  const [loadedStorageKey, setLoadedStorageKey] = useState(null);
  const offersRef = useRef(offers);
  const storageKey = getStorageKey(userId);
  const storageLoaded = Boolean(storageKey) && loadedStorageKey === storageKey;
  const meta = useMemo(() => {
    const totalItems = offers.filter(offer => offer.status === 'private').length;
    const totalPages = Math.ceil(totalItems / OFFERS_LIMIT);

    return {
      page: Math.min(offersPage, Math.max(totalPages, 1)),
      limit: OFFERS_LIMIT,
      totalPages,
      totalItems
    };
  }, [offers, offersPage]);

  useEffect(() => {
    offersRef.current = offers;
  }, [offers]);

  useEffect(() => {
    setOffers([]);
    setOffersPageState(1);
    setActiveOfferId(null);
    setActiveOfferStorage(null);
    setCurrentDestinationState(null);
    setLoadedStorageKey(null);

    if (!storageKey) return;

    try {
      let storedValue = window.localStorage.getItem(storageKey);

      if (storedValue === null) {
        const legacyStoredValue = window.localStorage.getItem(LEGACY_STORAGE_KEY);
        if (legacyStoredValue !== null) {
          JSON.parse(legacyStoredValue);
          window.localStorage.setItem(storageKey, legacyStoredValue);
          window.localStorage.removeItem(LEGACY_STORAGE_KEY);
          storedValue = legacyStoredValue;
        }
      }

      const storedState = storedValue === null ? null : JSON.parse(storedValue);
      if (storedState) {
        const storedOffers = Array.isArray(storedState)
          ? storedState
          : storedState.offers || [];
        const storedMeta = Array.isArray(storedState) ? null : storedState.meta;
        setOffers(storedOffers.map(normalizeOffer));
        setOffersPageState(Math.max(1, Number(storedMeta?.page) || 1));
        const storedActiveOfferId = normalizeOfferId(storedState.activeOfferId) || null;
        setActiveOfferId(storedActiveOfferId);
        setActiveOfferStorage(
          storedState.activeOfferStorage
          || (storedActiveOfferId ? 'private' : null)
        );
        setCurrentDestinationState(storedState.currentDestination?.type === 'offer'
          ? {
            ...storedState.currentDestination,
            id: normalizeOfferId(storedState.currentDestination.id)
          }
          : storedState.currentDestination || null);
      }
    } catch (e) {
      // Ignore unavailable or invalid browser storage and start with an empty draft state.
    } finally {
      setLoadedStorageKey(storageKey);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!storageLoaded || !storageKey) {
      return;
    }
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({
        offers,
        meta,
        activeOfferId,
        activeOfferStorage,
        currentDestination
      }));
    } catch (e) {
      // Drafts remain available for the current session if browser storage is unavailable.
    }
  }, [offers, meta, activeOfferId, activeOfferStorage, currentDestination, storageKey, storageLoaded]);

  const setOffersPage = useCallback(page => {
    setOffersPageState(Math.max(1, Number(page) || 1));
  }, []);

  const setCurrentDestination = useCallback(destination => {
    setCurrentDestinationState(destination);
  }, []);

  const setActiveOffer = useCallback((id, storage = 'private') => {
    if (!id) {
      setActiveOfferId(null);
      setActiveOfferStorage(null);
      setCurrentDestinationState(previousDestination => (
        previousDestination?.type === 'offer' ? null : previousDestination
      ));
      return true;
    }
    const offer = storage === 'private'
      ? offersRef.current.find(item => item.id === id)
      : null;
    if (storage === 'private' && (!offer || isOfferExpired(offer))) {
      return false;
    }
    setActiveOfferId(id);
    setActiveOfferStorage(storage);
    setCurrentDestinationState({ type: 'offer', id, storage });
    return true;
  }, []);

  const validateOfferProducts = useCallback(async products => {
    const productIds = [...new Set(products.map(getProductId).filter(Boolean).map(String))];
    if (!productIds.length) return;

    const query = productIds
      .map(productId => `productIds[]=${ encodeURIComponent(productId) }`)
      .join('&');
    // An object path and noCancel skip GET de-duping/aborting, which resolve superseded calls with null.
    const response = await fetcher(
      { url: `${ API_OFFER_PRODUCTS_DETAILS }?${ query }` },
      { noCancel: true }
    );
    const validationError = getProductValidationError(productIds, response);
    if (validationError) throw validationError;
  }, [fetcher]);

  const createOffer = useCallback(async (data, product) => {
    const createdAt = new Date();
    const now = createdAt.toISOString();
    const id = generateOfferId();
    const offerNumber = generateOfferNumber();
    const products = (Array.isArray(product) ? product : [product]).filter(Boolean);
    if (products.some(isProductUnavailableForOffer)) {
      throw new Error('Product is not available for offers.');
    }
    await validateOfferProducts(products);
    const defaultMarkupPercent = clampMarkupPercent(data.defaultMarkupPercent ?? 0);
    const items = products
      .map(normalizeOfferProduct)
      .map(item => ({
        ...item,
        markupPercent: item.markupPercent === null || item.markupPercent === undefined
          ? defaultMarkupPercent
          : clampMarkupPercent(item.markupPercent)
      }));
    const currencyId = String(data.currencyId || items.at(0)?.currency || 'ron').toLowerCase();
    const offer = withOfferSummary({
      id,
      offerNumber,
      name: data.name,
      clientName: data.clientName || '',
      clientEmail: data.clientEmail || '',
      validUntil: getOfferValidUntil(createdAt),
      currencyId,
      exchangeRate: Number(data.exchangeRate) > 0
        ? Number(data.exchangeRate)
        : 1,
      defaultMarkupPercent,
      status: 'private',
      sourceWishlist: data.sourceWishlist || null,
      createdAt: now,
      updatedAt: now,
      items
    });

    setOffers(previousOffers => previousOffers.concat(offer));
    if (data.setActive !== false) {
      setActiveOfferId(offer.id);
      setActiveOfferStorage('private');
      setCurrentDestinationState({ type: 'offer', id: offer.id, storage: 'private' });
    }
    return offer;
  }, [validateOfferProducts]);

  const updateOffer = useCallback((offerId, changes) => {
    setOffers(previousOffers => previousOffers.map(offer => {
      if (offer.id !== offerId) {
        return offer;
      }
      const nextChanges = typeof changes === 'function' ? changes(offer) : changes;
      return withOfferSummary({
        ...offer,
        ...nextChanges,
        updatedAt: new Date().toISOString()
      });
    }));
  }, []);

  const addProductToOffer = useCallback(async (offerId, product, options = {}) => {
    const findOffer = () => offersRef.current.find(item => item.id === offerId);
    const initialOffer = findOffer();
    if (!initialOffer) {
      return null;
    }
    const initialBlockedResult = getAddProductBlockedResult(initialOffer, product, options);
    if (initialBlockedResult) {
      return initialBlockedResult;
    }

    await validateOfferProducts([product]);

    // The offer may have changed, expired, or received this product while validating.
    const offer = findOffer();
    if (!offer) {
      return null;
    }
    const blockedResult = getAddProductBlockedResult(offer, product, options);
    if (blockedResult) {
      return blockedResult;
    }

    const normalizedProduct = normalizeOfferProduct(product);
    const withProduct = targetOffer => withOfferSummary({
      ...targetOffer,
      updatedAt: new Date().toISOString(),
      items: targetOffer.items.concat({
        ...normalizedProduct,
        markupPercent: normalizedProduct.markupPercent === null
          || normalizedProduct.markupPercent === undefined
          ? clampMarkupPercent(targetOffer.defaultMarkupPercent ?? 0)
          : clampMarkupPercent(normalizedProduct.markupPercent)
      })
    });
    const updatedOffer = withProduct(offer);
    // Sync the ref now so concurrent adds see this product before the next commit.
    offersRef.current = offersRef.current.map(item => (
      item.id === offerId ? updatedOffer : item
    ));
    setOffers(previousOffers => previousOffers.map(item => (
      item.id === offerId && !hasOfferProduct(item, product)
        ? withProduct(item)
        : item
    )));
    return { offer: updatedOffer, alreadyExists: false };
  }, [validateOfferProducts]);

  const updateOfferProduct = useCallback((offerId, productId, changes) => {
    setOffers(previousOffers => previousOffers.map(offer => {
      if (offer.id !== offerId) {
        return offer;
      }
      return withOfferSummary({
        ...offer,
        updatedAt: new Date().toISOString(),
        items: offer.items.map(product => (
          getProductId(product) === productId
            ? { ...product, ...changes }
            : product
        ))
      });
    }));
  }, []);

  const removeProductFromOffer = useCallback((offerId, productId) => {
    setOffers(previousOffers => previousOffers.map(offer => (
      offer.id === offerId
        ? withOfferSummary({
          ...offer,
          updatedAt: new Date().toISOString(),
          items: offer.items.filter(
            product => getProductId(product) !== productId
          )
        })
        : offer
    )));
  }, []);

  const deleteOffer = useCallback(offerId => {
    setOffers(previousOffers => previousOffers.filter(offer => offer.id !== offerId));
    setActiveOfferId(previousId => {
      if (activeOfferStorage === 'private' && previousId === offerId) {
        setActiveOfferStorage(null);
        return null;
      }
      return previousId;
    });
    setCurrentDestinationState(previousDestination => (
      previousDestination?.type === 'offer' && previousDestination.id === offerId
        ? null
        : previousDestination
    ));
  }, [activeOfferStorage]);

  const moveOfferToSaved = useCallback((privateOfferId, savedOfferId) => {
    const wasActiveOffer = activeOfferStorage === 'private'
      && String(activeOfferId) === String(privateOfferId);

    if (wasActiveOffer) {
      setActiveOfferId(savedOfferId);
      setActiveOfferStorage('saved');
    }
    setCurrentDestinationState(previousDestination => (
      previousDestination?.type === 'offer'
      && String(previousDestination.id) === String(privateOfferId)
        ? { type: 'offer', id: savedOfferId, storage: 'saved' }
        : previousDestination
    ));
    setOffers(previousOffers => previousOffers.filter(
      offer => String(offer.id) !== String(privateOfferId)
    ));
  }, [activeOfferId, activeOfferStorage]);

  const activeOffer = offers.find(
    offer => activeOfferStorage === 'private'
      && offer.id === activeOfferId
      && !isOfferExpired(offer)
  ) || null;
  const resolvedActiveOfferId = activeOfferStorage === 'saved'
    ? activeOfferId
    : activeOffer?.id || null;

  const value = useMemo(() => ({
    offers: storageLoaded ? offers : [],
    meta: storageLoaded ? meta : EMPTY_OFFERS_META,
    activeOffer: storageLoaded ? activeOffer : null,
    activeOfferId: storageLoaded ? resolvedActiveOfferId : null,
    activeOfferStorage: storageLoaded && resolvedActiveOfferId ? activeOfferStorage : null,
    isHydrated: storageLoaded,
    currentDestination: storageLoaded ? currentDestination : null,
    createOffer,
    updateOffer,
    addProductToOffer,
    updateOfferProduct,
    removeProductFromOffer,
    deleteOffer,
    moveOfferToSaved,
    setOffersPage,
    setActiveOffer,
    setCurrentDestination
  }), [
    offers,
    meta,
    activeOffer,
    activeOfferStorage,
    resolvedActiveOfferId,
    storageLoaded,
    currentDestination,
    createOffer,
    updateOffer,
    addProductToOffer,
    updateOfferProduct,
    removeProductFromOffer,
    deleteOffer,
    moveOfferToSaved,
    setOffersPage,
    setActiveOffer,
    setCurrentDestination
  ]);

  return (
    <OffersContext.Provider value={ value }>
      { children }
    </OffersContext.Provider>
  );
});

OffersProvider.propTypes = {
  children: node
};

OffersProvider.displayName = 'OffersProvider';

export default OffersProvider;
